import { createSlice } from '@reduxjs/toolkit'
import { FirestoreDatabase } from '../index'
import { Collection, AttendeeRoles, SpeakerRoles, orgRepresentativeStatus } from 'constants/index'
import { caseInsensitiveSort } from 'utils'
import { signOut } from 'store/auth'
import { chunk, first } from 'lodash'
import { toast } from 'utils/notify'
import moment from 'moment'
import { placeImageToDifferentPath, deleteImageFromStorageIfExist } from 'utils/subject'

const DEFAULT_ROLE_FILTER = 'all'
export const initialState = {
  error: null,
  inProgress: false,
  roleFilter: DEFAULT_ROLE_FILTER,
  attendees: [],
  confFilter: '',
  currentPage: 0,
  pageSize: 10,
  attendee: null
}

const attendeeSlice = createSlice({
  name: 'attendee',
  initialState: initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload
    },
    setProgress: (state, action) => {
      state.inProgress = action.payload
    },
    setRoleFilter: (state, action) => {
      state.roleFilter = action.payload
    },
    setConferenceFilter: (state, action) => {
      state.confFilter = action.payload
    },
    setAttendees: (state, action) => {
      state.attendees = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    },
    setAttendee: (state, action) => {
      state.attendee = action.payload
    }
  },
  extraReducers: {
    [signOut.fulfilled]: (state) => {
      state.roleFilter = DEFAULT_ROLE_FILTER
      state.error = null
      state.inProgress = false
      state.attendees = []
      state.confFilter = ''
      state.currentPage = 0
      state.pageSize = 10
      state.attendee = null
    }
  }
})

export const fetchAttendees = (conferenceId) => async (dispatch) => {
  try {
    dispatch(attendeeSlice.actions.setProgress(true))
    const isDataFetched = false
    const unsubscribe = await FirestoreDatabase.collection(Collection.ATTENDEES)
      .where('conferenceId', '==', conferenceId)
      .onSnapshot((documents) => {
        const attendees = []
        documents.forEach((doc) => {
          attendees.push({
            id: doc.id,
            ...doc.data()
          })
        })
        dispatch(attendeeSlice.actions.setAttendees(caseInsensitiveSort(attendees, 'firstName')))
        if (!isDataFetched) dispatch(attendeeSlice.actions.setProgress(false))
      })
    return () => unsubscribe()
  } catch (err) {
    dispatch(attendeeSlice.actions.setError(err))
  }
}

export const updateAttendee = async (attendeeId, data, onSuccess, onError) => {
  try {
    await FirestoreDatabase.collection(Collection.ATTENDEES).doc(attendeeId).update(data)
    const attendeeDeviceSnap = await FirestoreDatabase.collection(Collection.ATTENDEE_DEVICE)
      .where('attendeeId', '==', attendeeId)
      .get()

    if (!attendeeDeviceSnap?.empty) {
      const attendeeDevice = first(attendeeDeviceSnap.docs)
      const device = { id: attendeeDevice?.id, ...attendeeDevice.data() }
      await FirestoreDatabase.collection(Collection.ATTENDEE_DEVICE).doc(device?.id).update({
        attendeeRole: data?.role
      })
    }
    onSuccess && onSuccess()
  } catch (err) {
    console.error(err)
    onError && onError()
  }
}

export const fetchAttendeeById = (attendeeId) => async (dispatch) => {
  try {
    dispatch(attendeeSlice.actions.setProgress(true))
    const snapshot = await FirestoreDatabase.collection(Collection.ATTENDEES).doc(attendeeId).get()
    dispatch(
      attendeeSlice.actions.setAttendee({
        id: snapshot.id,
        ...snapshot.data()
      })
    )
    dispatch(attendeeSlice.actions.setProgress(false))
  } catch (err) {
    dispatch(attendeeSlice.actions.setError(err))
    dispatch(attendeeSlice.actions.setProgress(false))
  }
}

export const bulkImportAttendees = (attendeesData, conferenceId) => async (dispatch) => {
  try {
    // Validation: ensure we have an array
    if (!Array.isArray(attendeesData) || attendeesData.length === 0) {
      toast.error('Invalid representative data. Please check and retry.')
      return { added: 0, existing: 0 }
    }

    // 1. Get all existing attendees
    const snapshot = await FirestoreDatabase.collection(Collection.ATTENDEES)
      .where('conferenceId', '==', conferenceId)
      .get()

    const existingAttendeesMap = {}

    snapshot.docs.forEach((doc) => {
      const data = doc.data()
      existingAttendeesMap[data?.email.toLowerCase()] = { id: doc.id, ...data }
    })

    // 2. Compare and filter existing vs. new reps based on email
    const newAttendees = []
    const existingAttendees = []

    for (const rep of attendeesData) {
      if (!rep.email) continue

      const normalizedEmail = rep.email.toLowerCase()

      // Check if this email already exists in our map of existing representatives
      if (existingAttendeesMap[normalizedEmail]) {
        existingAttendees.push(rep)
      } else {
        newAttendees.push(rep)
      }
    }

    // 3. Batch create new representatives using chunks (Firestore limit: 500 writes per batch)
    const batchSize = 500
    const batchedAttendees = chunk(newAttendees, batchSize)

    if (batchedAttendees.length > 0) {
      const batchedPromises = batchedAttendees.map((attendeesBatch) => {
        const batch = FirestoreDatabase.batch()

        attendeesBatch.forEach((item) => {
          const docRef = FirestoreDatabase.collection(Collection.ATTENDEES).doc()
          batch.set(docRef, item)
        })
        return batch.commit()
      })

      // Execute all batch operations
      await Promise.all(batchedPromises)
    }

    // Refresh the attendees list
    dispatch(fetchAttendees(conferenceId))

    // 4. Return counts of new and existing reps
    return { added: newAttendees.length, existing: existingAttendees.length }
  } catch (error) {
    console.error('Error in bulkImportAttendees:', error)
    toast.error('Could not create representatives.')
    return { added: 0, existing: 0 }
  }
}

export const createOrUpdateAttendeeForSpeaker = async (
  speakerData,
  conferenceId,
  existingAttendeeId = null
) => {
  try {
    const roles = speakerData.roles || []
    const attendeeRole = roles.includes(SpeakerRoles.MODERATOR)
      ? AttendeeRoles.MODERATOR
      : AttendeeRoles.SPEAKER

    const syncedFields = {
      firstName: speakerData.firstName || '',
      lastName: speakerData.lastName || '',
      email: speakerData.email || '',
      credential: speakerData.credential || '',
      position: speakerData.position || '',
      company: speakerData.company || '',
      city: speakerData.city || '',
      state: speakerData.state || '',
      country: speakerData.country || '',
      phoneNumber: speakerData.phone || '',
      bio: speakerData.bio || '',
      websiteUrl: speakerData.websiteUrl || '',
      role: attendeeRole,
      conferenceId
    }

    // Resolve existing attendee doc (by explicit ID or by email lookup)
    let existingAttendeeDoc = null
    if (existingAttendeeId) {
      const snap = await FirestoreDatabase.collection(Collection.ATTENDEES)
        .doc(existingAttendeeId)
        .get()
      if (snap.exists) existingAttendeeDoc = snap
    } else {
      const existing = await FirestoreDatabase.collection(Collection.ATTENDEES)
        .where('email', '==', speakerData.email)
        .where('conferenceId', '==', conferenceId)
        .get()
      if (!existing.empty) existingAttendeeDoc = existing.docs[0]
    }

    // Copy profile picture from speaker storage path to attendee storage path if present
    if (speakerData.imageName) {
      // If attendee already exists and has a different image, delete the old one first
      if (existingAttendeeDoc) {
        const oldImageName = existingAttendeeDoc.data().imageName
        if (oldImageName && oldImageName !== speakerData.imageName) {
          await deleteImageFromStorageIfExist(
            `images/${conferenceId}/${Collection.ATTENDEES}/${oldImageName}`
          )
        }
      }
      const sourcePath = `images/${conferenceId}/${Collection.SPEAKERS}/${speakerData.imageName}`
      const destinationPath = `images/${conferenceId}/${Collection.ATTENDEES}/${speakerData.imageName}`
      await placeImageToDifferentPath(sourcePath, destinationPath)
      syncedFields.imageName = speakerData.imageName
    }

    if (existingAttendeeDoc && existingAttendeeId) {
      await existingAttendeeDoc.ref.update(syncedFields)
      return existingAttendeeId
    }

    if (existingAttendeeDoc) {
      await existingAttendeeDoc.ref.update({
        ...syncedFields,
        speakerId: speakerData.speakerId || ''
      })
      return existingAttendeeDoc.id
    }

    const newDoc = await FirestoreDatabase.collection(Collection.ATTENDEES).add({
      ...syncedFields,
      speakerId: speakerData.speakerId || '',
      status: orgRepresentativeStatus.NOT_NOTIFIED,
      createdAt: moment().utc().valueOf()
    })
    return newDoc.id
  } catch (err) {
    console.error('Error creating/updating attendee for speaker:', err)
    return null
  }
}

export default attendeeSlice
